import React from "react";
import PropTypes from "prop-types";
import { Button, TextField, Typography, Chip } from "@orijinworks/frontend-commons";
import { FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch, Box } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import { COLORS } from "../constants";
import { useStyles } from "./ui.style";

const CHIP_COLOR_MAP = {
  direct: "primary",
  inherited: "secondary",
  active: "success",
  suspended: "default",
  inactive: "error",
  tag: "primary",
};

export const Badge = ({ type, children }) => {
  const color = CHIP_COLOR_MAP[type] || "default";
  const isInherited = type === "inherited";
  return (
    <Chip
      label={children}
      color={color}
      size="small"
      variant="outlined"
      sx={{
        borderRadius: '12px',
        fontSize: '0.7rem',
        padding: '2px 8px',
        whiteSpace: 'nowrap',
        cursor: 'default',
        '&:hover': { backgroundColor: 'inherit' },
        '&:focus': { backgroundColor: 'inherit' },
        ...(isInherited && {
          backgroundColor: COLORS.inheritedLt,
          borderColor: COLORS.inherited,
          '&:hover': { backgroundColor: COLORS.inheritedLt },
          '&:focus': { backgroundColor: COLORS.inheritedLt },
        }),
      }}
    />
  );
};

export const Btn = ({ children, variant = "contained", onClick, disabled, sx, size }) => (
  <Button
    variant={variant}
    color="primary"
    size={size || "medium"}
    onClick={onClick}
    disabled={disabled}
    sx={sx}
  >
    {children}
  </Button>
);

export const Inp = ({ label, value, onChange, disabled, placeholder, sx, multiline, rows }) => {
  const classes = useStyles();
  return (
    <Box className={classes.inpWrapper} sx={sx}>
      <TextField
        label={label}
        value={value || ""}
        onChange={(event) => onChange?.(event.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        fullWidth
        variant="outlined"
        size="small"
        multiline={multiline}
        rows={rows}
      />
    </Box>
  );
};

export const Sel = ({ label, value, options, onChange, disabled, sx }) => {
  const classes = useStyles();
  return (
    <FormControl fullWidth variant="outlined" size="small" className={classes.selWrapper} sx={sx}>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value ?? ""}
        onChange={(event) => onChange?.(event.target.value)}
        label={label}
        disabled={disabled}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

/**
 * Searchable single-select dropdown. Same option shape as Sel; use for long lists.
 * Options are filtered as the user types.
 */
export const SearchSel = ({ label, value, options, onChange, disabled, placeholder, sx }) => {
  const classes = useStyles();
  const selectedOption = options.find((opt) => opt.value === (value ?? "")) || null;
  return (
    <Box className={classes.searchSelWrapper} sx={sx}>
      <Autocomplete
        value={selectedOption}
        options={options}
        getOptionLabel={(opt) => opt.label}
        getOptionSelected={(opt, val) => opt.value === (val?.value)}
        onChange={(_, newValue) => onChange?.(newValue ? newValue.value : "")}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            size="small"
            placeholder={placeholder}
            fullWidth
          />
        )}
      />
    </Box>
  );
};

export const Toggle = ({ label, checked, onChange, disabled, sx }) => {
  const classes = useStyles();
  return (
    <Box className={classes.toggleWrapper} sx={sx}>
      <FormControlLabel
        control={
          <Switch
            checked={checked}
            onChange={() => onChange?.()}
            disabled={disabled}
            color="primary"
          />
        }
        label={label}
        labelPlacement="start"
        className={classes.toggleLabel}
      />
    </Box>
  );
};

Badge.propTypes = { type: PropTypes.string, children: PropTypes.node };
Badge.defaultProps = { type: "default", children: null };

Btn.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  sx: PropTypes.object,
  size: PropTypes.string,
};
Btn.defaultProps = {
  children: null,
  variant: "contained",
  onClick: undefined,
  disabled: false,
  sx: undefined,
  size: undefined,
};

Inp.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  sx: PropTypes.object,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
};
Inp.defaultProps = {
  label: undefined,
  value: "",
  onChange: undefined,
  disabled: false,
  placeholder: undefined,
  sx: undefined,
  multiline: false,
  rows: undefined,
};

Sel.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  sx: PropTypes.object,
};
Sel.defaultProps = {
  label: undefined,
  value: "",
  options: [],
  onChange: undefined,
  disabled: false,
  sx: undefined,
};

SearchSel.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  sx: PropTypes.object,
};
SearchSel.defaultProps = {
  label: undefined,
  value: "",
  options: [],
  onChange: undefined,
  disabled: false,
  placeholder: undefined,
  sx: undefined,
};

Toggle.propTypes = {
  label: PropTypes.string,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  sx: PropTypes.object,
};
Toggle.defaultProps = {
  label: undefined,
  checked: false,
  onChange: undefined,
  disabled: false,
  sx: undefined,
};

export const CollapsibleSection = ({ title, right, expanded, onToggle, children }) => {
  const classes = useStyles();
  return (
    <Box>
      <Box className={classes.sectionHeadingRow}>
        <button
          type="button"
          onClick={onToggle}
          className={classes.collapsibleToggleButton}
          aria-expanded={expanded}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: COLORS.headerText,
              fontSize: "0.8rem",
              fontWeight: 700,
              letterSpacing: 1.4,
              textTransform: "uppercase",
            }}
          >
            {title}
          </Typography>
          {expanded && <ExpandLessIcon className={classes.expandIcon} aria-hidden />}
          {!expanded && <ExpandMoreIcon className={classes.expandIcon} aria-hidden />}
        </button>
        {right}
      </Box>
      {expanded && children}
    </Box>
  );
};

CollapsibleSection.propTypes = {
  title: PropTypes.node.isRequired,
  right: PropTypes.node,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  children: PropTypes.node,
};
CollapsibleSection.defaultProps = { right: null, children: null };

export const SectionHeading = ({ children, right }) => {
  const classes = useStyles();
  return (
    <Box className={classes.sectionHeadingRow}>
      <Typography
        variant="subtitle2"
        sx={{
          color: COLORS.headerText,
          fontSize: "0.8rem",
          fontWeight: 700,
          letterSpacing: 1.4,
          textTransform: "uppercase",
        }}
      >
        {children}
      </Typography>
      {right}
    </Box>
  );
};

SectionHeading.propTypes = { children: PropTypes.node, right: PropTypes.node };
SectionHeading.defaultProps = { children: null, right: null };
